import { Add as AddIcon, Delete as DeleteIcon, DragIndicator as DragIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useRulesStore } from '../../stores/rulesStore';
import type { Preference } from '../../types';

type DialogMode = 'create' | 'edit' | null;

export const Preferences: React.FC = () => {
  const { preferences, addPreference, updatePreference, deletePreference, togglePreferenceActive, reorderPreferences } =
    useRulesStore();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedPreference, setSelectedPreference] = useState<Preference | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'teacher' as Preference['category'],
    impact: 'medium' as Preference['impact'],
  });

  const sortedPreferences = [...preferences].sort((a, b) => a.rank - b.rank);

  const handleCreate = () => {
    setFormData({ name: '', description: '', category: 'teacher', impact: 'medium' });
    setSelectedPreference(null);
    setDialogMode('create');
  };

  const handleEdit = (pref: Preference) => {
    setFormData({
      name: pref.name,
      description: pref.description,
      category: pref.category,
      impact: pref.impact,
    });
    setSelectedPreference(pref);
    setDialogMode('edit');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this preference?')) {
      deletePreference(id);
    }
  };

  const handleToggleActive = (id: string) => {
    togglePreferenceActive(id);
  };

  const handleSubmit = () => {
    const newPref: Preference = {
      id: selectedPreference?.id || `pref-${Date.now()}`,
      ...formData,
      rank: selectedPreference?.rank || preferences.length + 1,
      isActive: selectedPreference?.isActive ?? true,
      createdAt: selectedPreference?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    if (dialogMode === 'create') {
      addPreference(newPref);
    } else if (dialogMode === 'edit' && selectedPreference) {
      updatePreference(selectedPreference.id, newPref);
    }
    setDialogMode(null);
    setSelectedPreference(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedPreference(null);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...sortedPreferences];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    reorderPreferences(newOrder);
  };

  const moveDown = (index: number) => {
    if (index === sortedPreferences.length - 1) return;
    const newOrder = [...sortedPreferences];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    reorderPreferences(newOrder);
  };

  const getCategoryColor = (category: Preference['category']) => {
    const colors: Record<Preference['category'], 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error'> = {
      teacher: 'primary',
      room: 'secondary',
      student: 'success',
      curriculum: 'warning',
      collaboration: 'error',
      scheduling: 'info',
    };
    return colors[category];
  };

  const getImpactColor = (impact: Preference['impact']) => {
    const colors: Record<Preference['impact'], 'error' | 'warning' | 'info'> = {
      high: 'error',
      medium: 'warning',
      low: 'info',
    };
    return colors[impact];
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1">
              Institutional Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ranked school-wide priorities that guide timetable generation. Drag to reorder.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Add Preference
          </Button>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Total Preferences
            </Typography>
            <Typography variant="h3">{preferences.length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Active
            </Typography>
            <Typography variant="h3">{preferences.filter((p) => p.isActive).length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              High Impact
            </Typography>
            <Typography variant="h3">{preferences.filter((p) => p.impact === 'high').length}</Typography>
          </Paper>
        </Box>

        {/* Preferences List */}
        <Paper>
          <List>
            {sortedPreferences.map((pref, index) => (
              <ListItem
                key={pref.id}
                sx={{
                  borderBottom: index < sortedPreferences.length - 1 ? '1px solid #eee' : 'none',
                  bgcolor: pref.isActive ? 'transparent' : '#f5f5f5',
                }}
              >
                <ListItemIcon>
                  <DragIcon />
                </ListItemIcon>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mr: 2 }}>
                  <Button size="small" onClick={() => moveUp(index)} disabled={index === 0}>
                    ▲
                  </Button>
                  <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>
                    {pref.rank}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => moveDown(index)}
                    disabled={index === sortedPreferences.length - 1}
                  >
                    ▼
                  </Button>
                </Box>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">{pref.name}</Typography>
                      <Chip label={pref.category} color={getCategoryColor(pref.category)} size="small" />
                      <Chip label={pref.impact} color={getImpactColor(pref.impact)} size="small" />
                    </Box>
                  }
                  secondary={pref.description}
                />
                <Chip
                  label={pref.isActive ? 'Active' : 'Inactive'}
                  color={pref.isActive ? 'success' : 'default'}
                  size="small"
                  onClick={() => handleToggleActive(pref.id)}
                  sx={{ cursor: 'pointer', mr: 1 }}
                />
                <IconButton size="small" onClick={() => handleEdit(pref)}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(pref.id)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogMode !== null} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{dialogMode === 'create' ? 'Create New Preference' : 'Edit Preference'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Preference Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
                required
              />
              <TextField
                select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as Preference['category'] })}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="teacher">Teacher</option>
                <option value="student">Student</option>
                <option value="room">Room</option>
                <option value="curriculum">Curriculum</option>
                <option value="collaboration">Collaboration</option>
                <option value="scheduling">Scheduling</option>
              </TextField>
              <TextField
                select
                label="Impact"
                value={formData.impact}
                onChange={(e) => setFormData({ ...formData, impact: e.target.value as Preference['impact'] })}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Preferences;
